import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Edit, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const InventoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/inventory/${id}`);
        setRecord(res.data);
      } catch (error) {
        toast.error('Failed to load inventory record');
        navigate('/admin/inventory');
      } finally {
        setLoading(false);
      }
    };
    fetchRecord();
  }, [id, navigate]);

  const handlePrint = () => window.print();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) return <div className="min-h-screen pt-44 px-4 flex justify-center"><p>Loading...</p></div>;
  if (!record) return null;

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-5xl">
        <div className="flex justify-between items-center mb-8 print:hidden">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/inventory')}>
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ClipboardList className="text-blue-600" /> {record.inventory_number}
              </h1>
              <p className="text-gray-600">Inventory Stock Take Record</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/admin/inventory/${id}/edit`)}>
              <Edit size={16} className="mr-2" /> Edit
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer size={16} className="mr-2" /> Print
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-8 border-b pb-6">
              <h2 className="text-2xl font-bold text-gray-900">NJK Logistics Pte Ltd</h2>
              <p className="text-gray-600">212A Punggol Walk #12-741 Singapore 821212</p>
              <p className="text-gray-600">Tel: 98366186</p>
              <h3 className="text-xl font-bold mt-4 text-blue-600">INVENTORY STOCK TAKE</h3>
            </div>

            {/* Customer & Inventory Info */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-800 border-b pb-1">Customer Details</h4>
                {record.company_name && <p><span className="text-gray-500">Company:</span> {record.company_name}</p>}
                <p><span className="text-gray-500">Name:</span> {record.customer_name}</p>
                {record.customer_address && <p><span className="text-gray-500">Address:</span> {record.customer_address}</p>}
                {record.person_in_charge && <p><span className="text-gray-500">Person In Charge:</span> {record.person_in_charge}</p>}
                {record.customer_phone && <p><span className="text-gray-500">Phone:</span> {record.customer_phone}</p>}
                {record.customer_email && <p><span className="text-gray-500">Email:</span> {record.customer_email}</p>}
              </div>
              <div className="space-y-2 text-right">
                <h4 className="font-semibold text-gray-800 border-b pb-1">Inventory Info</h4>
                <p><span className="text-gray-500">Inventory No:</span> <span className="font-semibold">{record.inventory_number}</span></p>
                <p><span className="text-gray-500">Date:</span> {formatDate(record.inventory_date)}</p>
                <p><span className="text-gray-500">Status:</span> <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${record.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{record.status?.toUpperCase()}</span></p>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Stock Take Items:</h3>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-center w-10">#</th>
                    <th className="border p-2 text-left">Description</th>
                    <th className="border p-2 text-left">SKU / Reg No</th>
                    <th className="border p-2 text-left">Zone</th>
                    <th className="border p-2 text-left">Address</th>
                    <th className="border p-2 text-center">Qty</th>
                    <th className="border p-2 text-center">Record Qty</th>
                    <th className="border p-2 text-center">IN</th>
                    <th className="border p-2 text-center">OUT</th>
                    <th className="border p-2 text-center font-bold">Total Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {record.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="border p-2 text-center text-gray-500">{index + 1}</td>
                      <td className="border p-2">{item.description}</td>
                      <td className="border p-2">{item.sku_reg_no || '-'}</td>
                      <td className="border p-2">{item.zone || '-'}</td>
                      <td className="border p-2">{item.address || '-'}</td>
                      <td className="border p-2 text-center">{item.qty}</td>
                      <td className="border p-2 text-center">{item.record_qty}</td>
                      <td className="border p-2 text-center text-green-600 font-medium">{item.qty_in || 0}</td>
                      <td className="border p-2 text-center text-red-600 font-medium">{item.qty_out || 0}</td>
                      <td className="border p-2 text-center font-bold">{item.total_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            {record.notes && (
              <div className="mt-6 p-4 bg-gray-50 rounded">
                <h4 className="font-semibold text-gray-800 mb-2">Notes:</h4>
                <p className="text-gray-600 whitespace-pre-wrap">{record.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InventoryDetail;
